import { get, post } from '../core/client';
import { getConfig, getShopId, isInitialized } from '../core/config';
import { buildEndpoint } from '../core/endpoint';
import { getStorefrontConnectionId } from '../core/telemetry';
import { resolveUtmParameters } from '../core/attribution';
import type { SDKResponse, StorefrontOnlineUsers, StorefrontRecentSales } from '../types';

/**
 * Anonymized recent-sales feed for storefront social proof (product title +
 * relative age only — never buyer identity). Empty items when the shop has no
 * recent completed orders.
 */
export async function getStorefrontRecentSales(): Promise<SDKResponse<StorefrontRecentSales>> {
  if (!isInitialized()) {
    return { success: false, message: 'SDK not initialized' };
  }

  const config = getConfig();
  const shopId = getShopId();
  const endpoint = shopId
    ? buildEndpoint('/v1/storefront/shops/id/:id/recent-sales', { id: shopId })
    : buildEndpoint('/v1/storefront/shops/:storeSlug/recent-sales', {
        storeSlug: config.storeSlug,
      });

  return get<StorefrontRecentSales>(endpoint, {
    cache: false,
    retries: 0,
    timeout: 5000,
  });
}

export async function getStorefrontOnlineUsers(): Promise<SDKResponse<StorefrontOnlineUsers>> {
  if (!isInitialized()) {
    return { success: false, message: 'SDK not initialized' };
  }

  const config = getConfig();
  const shopId = getShopId();
  const endpoint = shopId
    ? buildEndpoint('/v1/storefront/shops/id/:id/online-users', { id: shopId })
    : buildEndpoint('/v1/storefront/shops/:storeSlug/online-users', {
        storeSlug: config.storeSlug,
      });

  return get<StorefrontOnlineUsers>(endpoint, {
    cache: false,
    retries: 0,
    timeout: 5000,
  });
}

export async function touchStorefrontPresence(): Promise<SDKResponse<{ pong: string }>> {
  if (!isInitialized()) {
    return { success: false, message: 'SDK not initialized' };
  }
  if (typeof document === 'undefined') {
    return { success: false, message: 'Document is not available' };
  }

  const config = getConfig();
  const shopId = getShopId();
  const endpoint = shopId
    ? buildEndpoint('/v1/storefront/shops/id/:id/ping', { id: shopId })
    : buildEndpoint('/v1/storefront/shops/:storeSlug/ping', {
        storeSlug: config.storeSlug,
      });

  return post<{ pong: string }>(
    endpoint,
    {
      referer: document.referrer || undefined,
      connection_id: getStorefrontConnectionId(config.storeSlug) ?? undefined,
      utm: resolveUtmParameters(config.storeSlug),
    },
    {
      retries: 0,
      timeout: 5000,
    },
  );
}
